import { registerLazy } from '@norkron/web-conformance/register-lazy';
import { z } from 'zod';
import { memo, useMemo, useState, useId, useRef, useCallback, useEffect, Fragment, useLayoutEffect } from 'react';
import { useFocusRestore, useArrowKeyNavigation } from '@norkron/web-a11y';
import { useThemeVar } from '@norkron/web-themer/react';
import { withConformance, registerResponsiveContract } from '@norkron/web-conformance';
import { createScopedErrors, isCodeRegistered, registerCodes } from '@norkron/web-errors';
import { jsx, jsxs, Fragment as Fragment$1 } from 'react/jsx-runtime';
import { RichTooltip } from '@norkron/web-tooltip';
import { LoadingState, EmptyState } from '@norkron/web-states';
import { formatBytes as formatBytes$1 } from '@norkron/web-numeric-format';
import { ErrorBoundary, DefaultErrorFallback } from '@norkron/web-error-boundary';

// src/index.ts
var SelfCheckOptionsSchema = z.object({
  budgetMs: z.number().int().positive().max(5e3).optional(),
  minSeverity: z.enum(["critical", "major", "minor", "info"]).optional(),
  locale: z.string().min(2).max(35).optional()
}).strict();

// src/validate-self-check.ts
var DEFAULT_BUDGET_MS = 50;
function parseSelfCheckOptions(opts) {
  const parsed = SelfCheckOptionsSchema.safeParse(opts ?? {});
  if (parsed.success) {
    return {
      budgetMs: parsed.data.budgetMs ?? DEFAULT_BUDGET_MS,
      minSeverity: parsed.data.minSeverity ?? "info"
    };
  }
  return { budgetMs: DEFAULT_BUDGET_MS, minSeverity: "info" };
}

// src/styles.ts
var STYLE_ELEMENT_ID = "norkron-web-admin-shell-styles";
var STYLE_OPT_OUT_ATTR = "data-norkron-admin-shell-styles";
var BEM_BLOCKS = [
  "admin-shell",
  "top-nav",
  "top-nav-link",
  "badge-pill",
  "kpi-card",
  "stat-strip",
  "data-panel",
  "delta-value",
  "view-container",
  "page-header"
];
var TOKEN_CONTRACT = [
  // surface + text + border. `--norkron-surface-base` (the page canvas) is
  // deliberately absent: no component in this lib paints a page background —
  // panels/cards use `-elevated`, chrome uses `-sunken`, and everything else
  // stays transparent so it inherits the consumer's canvas.
  "--norkron-surface-elevated",
  "--norkron-surface-sunken",
  "--norkron-text-primary",
  "--norkron-text-muted",
  "--norkron-text-on-accent",
  "--norkron-border-default",
  "--norkron-border-strong",
  "--norkron-accent",
  // state ramps (soft background is always paired with its own on-soft fg)
  "--norkron-state-info",
  "--norkron-state-info-soft",
  "--norkron-state-info-on-soft",
  "--norkron-state-success",
  "--norkron-state-success-soft",
  "--norkron-state-success-on-soft",
  "--norkron-state-warning",
  "--norkron-state-warning-soft",
  "--norkron-state-warning-on-soft",
  "--norkron-state-danger",
  "--norkron-state-danger-soft",
  "--norkron-state-danger-on-soft",
  "--norkron-state-announcement",
  "--norkron-state-announcement-soft",
  "--norkron-state-announcement-on-soft",
  // geometry / type / motion
  "--norkron-space-0",
  "--norkron-space-1",
  "--norkron-space-2",
  "--norkron-space-3",
  "--norkron-space-4",
  "--norkron-space-5",
  "--norkron-radius-sm",
  "--norkron-radius-md",
  "--norkron-radius-lg",
  "--norkron-radius-full",
  "--norkron-font-size-xs",
  "--norkron-font-size-sm",
  "--norkron-font-size-md",
  "--norkron-font-size-xl",
  "--norkron-font-size-2xl",
  "--norkron-font-size-3xl",
  "--norkron-font-mono",
  "--norkron-font-sans",
  "--norkron-measure-default",
  "--norkron-shadow-sm",
  "--norkron-focus-ring-width",
  "--norkron-focus-ring-offset",
  "--norkron-opacity-muted",
  "--norkron-opacity-strong",
  "--norkron-z-sticky"
];
var ADMIN_SHELL_STYLES = `
/* @norkron/web-admin-shell \u2014 generated from src/styles.ts. Do not edit dist. */

/* \u2500\u2500 Layer 2: component token bridge \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
 * Zero-specificity defaults, so any consumer declaration wins. Each value
 * chains: component var (none yet) -> --norkron-* theme token -> CSS system
 * colour. No hex literals anywhere in this file.
 */
:where(:root) {
	/* shared scale aliases \u2014 the two type steps below --norkron-font-size-xs
	 * (12px) that dashboards need for badge/hint text. */
	--admin-shell__font-3xs: 10px;
	--admin-shell__font-2xs: 11px;
	--admin-shell__transition: 120ms;

	/* surface / text / border */
	--admin-shell__surface: var(--norkron-surface-elevated, Canvas);
	--admin-shell__surface-sunken: var(--norkron-surface-sunken, Canvas);
	--admin-shell__fg: var(--norkron-text-primary, CanvasText);
	--admin-shell__fg-muted: var(--norkron-text-muted, GrayText);
	--admin-shell__border: var(--norkron-border-default, ButtonBorder);
	--admin-shell__border-strong: var(--norkron-border-strong, ButtonBorder);
	--admin-shell__accent: var(--norkron-accent, AccentColor);
	--admin-shell__on-accent: var(--norkron-text-on-accent, AccentColorText);

	/* top-nav */
	--top-nav-bg: var(--admin-shell__surface);
	--top-nav-fg: var(--admin-shell__fg);
	--top-nav-fg-muted: var(--admin-shell__fg-muted);
	--top-nav-border: var(--admin-shell__border);
	--top-nav-hover: var(--admin-shell__surface-sunken);
	--top-nav-ring: var(--admin-shell__accent);
	--top-nav-active-bg: var(--admin-shell__surface-sunken);
	--top-nav-active-fg: var(--admin-shell__accent);
	--top-nav-badge-bg: var(--admin-shell__surface-sunken);
	--top-nav-badge-fg: var(--admin-shell__fg);
	--top-nav-badge-danger: var(--norkron-state-danger, Mark);
	--top-nav-badge-danger-fg: var(--norkron-text-on-accent, MarkText);
	/* Viewport tier at which the nav rows stop collapsing behind the
	 * hamburger. Matches the canonical 'xl' breakpoint. Consumers can widen
	 * or narrow it, but the value must be a <length> usable in a media query
	 * fallback \u2014 see the @media block at the end of this sheet. */
	--top-nav-expand-at: 1280px;

	/* data-panel */
	--data-panel-bg: var(--admin-shell__surface);
	--data-panel-border: var(--admin-shell__border);
	--data-panel-border-subtle: var(--admin-shell__border);
	--data-panel-text: var(--admin-shell__fg);
	--data-panel-text-muted: var(--admin-shell__fg-muted);
	--data-panel-accent-50: var(--norkron-state-info-soft, Canvas);
	--data-panel-accent-300: var(--norkron-state-info, ButtonBorder);
	--data-panel-accent-900: var(--norkron-state-info-on-soft, CanvasText);

	/* kpi-card */
	--kpi-card-bg: var(--admin-shell__surface);
	--kpi-card-border: var(--admin-shell__border);
	--kpi-card-text: var(--admin-shell__fg);
	--kpi-card-text-muted: var(--admin-shell__fg-muted);
	--kpi-card-accent: var(--admin-shell__accent);
	--kpi-card-delta-positive: var(--norkron-state-success, CanvasText);
	--kpi-card-delta-negative: var(--norkron-state-danger, CanvasText);

	/* stat-strip */
	--stat-strip-text: var(--admin-shell__fg);
	--stat-strip-text-muted: var(--admin-shell__fg-muted);

	/* delta-value */
	--delta-value-positive: var(--norkron-state-success, CanvasText);
	--delta-value-negative: var(--norkron-state-danger, CanvasText);
	--delta-value-neutral: var(--admin-shell__fg-muted);

	/* badge-pill \u2014 the 'admin' tone keeps its historical var names; they now
	 * resolve through the announcement ramp instead of literal pinks. */
	--badge-pill-ring: var(--admin-shell__accent);
	--badge-pill-admin-50: var(--norkron-state-announcement-soft, Canvas);
	--badge-pill-admin-300: var(--norkron-state-announcement, ButtonBorder);
	--badge-pill-admin-500: var(--norkron-state-announcement, CanvasText);
	--badge-pill-admin-600: var(--norkron-state-announcement, CanvasText);
	--badge-pill-admin-800: var(--norkron-state-announcement-on-soft, CanvasText);
	--badge-pill-admin-900: var(--norkron-state-announcement-on-soft, CanvasText);

	/* page-header */
	--page-header-fg: var(--admin-shell__fg);
	--page-header-fg-muted: var(--admin-shell__fg-muted);
	--page-header-border: var(--admin-shell__border);
	--page-header-title-size: var(--norkron-font-size-2xl, 24px);
	/* Page titles are the one place a product routinely wants a different face
	 * from its body text \u2014 products/elaborate sets its screen titles in a serif
	 * while the UI stays sans. Exposing the family as a var means adopting
	 * PageHeader is a one-line override instead of a selector fight \u2014 a consumer
	 * points this var at whatever serif token its theme defines. It is NOT
	 * defaulted to a serif token here on purpose: --norkron-font-serif does not
	 * exist in web-themer's base.css (elaborate defines it locally, in three
	 * places with three different stacks), and this sheet must not read a token
	 * that has no owner. See TOKEN_CONTRACT and token-contract.test.ts. */
	--page-header-title-font: var(--norkron-font-sans, inherit);
}

/* \u2500\u2500 shared utility (in-namespace, so the leak gate stays green) \u2500\u2500\u2500\u2500\u2500 */
:where(.admin-shell__visually-hidden) {
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
}

:where(.admin-shell__truncate) {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	min-width: 0;
}

/* \u2550\u2550 top-nav \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 */
:where(.top-nav) {
	box-sizing: border-box;
	width: 100%;
	color-scheme: light dark;
	background: var(--top-nav-bg);
	color: var(--top-nav-fg);
	border-bottom: 1px solid var(--top-nav-border);
	font-family: var(--norkron-font-sans, inherit);
}

:where(.top-nav--sticky) {
	position: sticky;
	top: 0;
	z-index: var(--norkron-z-sticky, 1100);
	backdrop-filter: blur(8px);
}

:where(.top-nav__bar) {
	display: flex;
	align-items: center;
	gap: var(--norkron-space-2, 12px);
	padding: 0 var(--norkron-space-3, 16px);
	/* 56px comfortable / 48px compact \u2014 both on the 4px grid, both above the
	 * WCAG 2.5.5 24px minimum for the affordances inside. */
	height: 56px;
}

:where(.top-nav--compact) :where(.top-nav__bar) {
	height: 48px;
}

:where(.top-nav__brand) {
	display: inline-flex;
	align-items: center;
	gap: var(--norkron-space-1, 8px);
	min-width: 0;
	font-weight: 600;
	color: var(--top-nav-fg);
	text-decoration: none;
}

:where(.top-nav__brand:hover) {
	opacity: var(--norkron-opacity-strong, 0.9);
}

:where(.top-nav__brand-logo) {
	display: inline-flex;
	flex-shrink: 0;
}

:where(.top-nav__brand-label) {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

:where(.top-nav__primary, .top-nav__secondary, .top-nav__actions) {
	/* collapsed by default; the @media block below expands them at the
	 * --top-nav-expand-at tier. This is the real breakpoint behaviour the
	 * lib used to borrow from a utility framework's viewport variants. */
	display: none;
	align-items: center;
	gap: var(--norkron-space-0, 4px);
	min-width: 0;
}

:where(.top-nav__primary) {
	margin-inline-start: var(--norkron-space-1, 8px);
}

:where(.top-nav__search) {
	display: none;
}

:where(.top-nav__right) {
	display: flex;
	align-items: center;
	gap: var(--norkron-space-1, 8px);
	margin-inline-start: auto;
}

:where(.top-nav__user-menu) {
	flex-shrink: 0;
	min-width: 0;
}

:where(.top-nav__action, .top-nav__hamburger) {
	position: relative;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 36px;
	height: 36px;
	padding: 0;
	background: none;
	border: 0;
	border-radius: var(--norkron-radius-sm, 4px);
	color: var(--top-nav-fg-muted);
	cursor: pointer;
	transition: background-color var(--admin-shell__transition) ease,
		color var(--admin-shell__transition) ease;
}

:where(.top-nav__action:hover, .top-nav__hamburger:hover) {
	background: var(--top-nav-hover);
	color: var(--top-nav-fg);
}

:where(.top-nav__action:focus-visible, .top-nav__hamburger:focus-visible) {
	outline: var(--norkron-focus-ring-width, 2px) solid var(--top-nav-ring);
	outline-offset: var(--norkron-focus-ring-offset, 2px);
}

:where(.top-nav__action-icon, .top-nav__hamburger-icon) {
	display: inline-flex;
	width: 20px;
	height: 20px;
}

:where(.top-nav__slot-above, .top-nav__slot-below) {
	display: block;
}

:where(.top-nav__action-badge) {
	position: absolute;
	top: -2px;
	inset-inline-end: -2px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 16px;
	height: 16px;
	padding: 0 var(--norkron-space-0, 4px);
	box-sizing: border-box;
	font-size: var(--admin-shell__font-3xs, 10px);
	font-weight: 600;
	line-height: 1;
	border-radius: var(--norkron-radius-full, 9999px);
	background: var(--top-nav-badge-danger);
	color: var(--top-nav-badge-danger-fg);
}

:where(.top-nav__drawer) {
	display: flex;
	flex-direction: column;
	gap: var(--norkron-space-0, 4px);
	padding: var(--norkron-space-2, 12px);
	background: var(--top-nav-bg);
	border-top: 1px solid var(--top-nav-border);
}

:where(.top-nav__drawer-search) {
	margin-bottom: var(--norkron-space-1, 8px);
}

:where(.top-nav__drawer-group) {
	display: flex;
	flex-direction: column;
	gap: var(--norkron-space-0, 4px);
	margin-top: var(--norkron-space-1, 8px);
	padding-top: var(--norkron-space-1, 8px);
	border-top: 1px solid var(--top-nav-border);
}

:where(.top-nav__drawer-actions) {
	display: flex;
	align-items: center;
	gap: var(--norkron-space-0, 4px);
	margin-top: var(--norkron-space-1, 8px);
	padding-top: var(--norkron-space-1, 8px);
	border-top: 1px solid var(--top-nav-border);
}

/* \u2500\u2500 top-nav-link \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */
:where(.top-nav-link) {
	display: inline-flex;
	align-items: center;
	gap: var(--norkron-space-0, 4px);
	max-width: 100%;
	min-width: 0;
	padding: var(--norkron-space-0, 4px) var(--norkron-space-1, 8px);
	/* \u226524px hit area (WCAG 2.5.5 AA) without pushing the 48px compact bar
	 * off-grid: 4px+4px padding + 16px line-box = 24px. */
	min-height: 24px;
	box-sizing: border-box;
	font-size: var(--norkron-font-size-sm, 14px);
	font-weight: 500;
	line-height: 16px;
	border-radius: var(--norkron-radius-sm, 4px);
	color: var(--top-nav-fg-muted);
	text-decoration: none;
	transition: background-color var(--admin-shell__transition) ease,
		color var(--admin-shell__transition) ease;
}

:where(.top-nav-link:hover) {
	background: var(--top-nav-hover);
	color: var(--top-nav-fg);
}

:where(.top-nav-link:focus-visible) {
	outline: var(--norkron-focus-ring-width, 2px) solid var(--top-nav-ring);
	outline-offset: var(--norkron-focus-ring-offset, 2px);
}

:where(.top-nav-link--active) {
	background: var(--top-nav-active-bg);
	color: var(--top-nav-active-fg);
}

:where(.top-nav-link--block) {
	width: 100%;
	justify-content: flex-start;
}

:where(.top-nav-link__icon) {
	display: inline-flex;
	flex-shrink: 0;
}

:where(.top-nav-link__label) {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

:where(.top-nav-link__badge) {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 18px;
	height: 18px;
	padding: 0 var(--norkron-space-0, 4px);
	box-sizing: border-box;
	font-family: var(--norkron-font-mono, monospace);
	font-size: var(--admin-shell__font-3xs, 10px);
	line-height: 1;
	border-radius: var(--norkron-radius-sm, 4px);
	background: var(--top-nav-badge-bg);
	color: var(--top-nav-badge-fg);
}

:where(.top-nav-link__external-icon) {
	width: 12px;
	height: 12px;
	opacity: var(--norkron-opacity-muted, 0.7);
}

/* \u2550\u2550 badge-pill \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 */
:where(.badge-pill) {
	display: inline-flex;
	align-items: center;
	vertical-align: middle;
	box-sizing: border-box;
	border: 1px solid transparent;
	font-family: inherit;
	font-weight: 500;
	white-space: nowrap;
	user-select: none;
	/* tone defaults; every tone modifier below overrides all four. */
	--badge-pill__soft-bg: var(--admin-shell__surface-sunken);
	--badge-pill__soft-fg: var(--admin-shell__fg);
	--badge-pill__solid-bg: var(--admin-shell__fg);
	--badge-pill__solid-fg: var(--admin-shell__surface);
	--badge-pill__line: var(--admin-shell__border-strong);
	--badge-pill__dot: var(--admin-shell__fg-muted);
}

/* sizes \u2014 padding + type step + intra-pill gap */
:where(.badge-pill--small) {
	gap: var(--norkron-space-0, 4px);
	padding: 2px var(--norkron-space-1, 8px);
	font-size: var(--norkron-font-size-xs, 12px);
	line-height: 16px;
}

:where(.badge-pill--medium) {
	gap: var(--norkron-space-0, 4px);
	padding: var(--norkron-space-0, 4px) var(--norkron-space-1, 8px);
	font-size: var(--norkron-font-size-xs, 12px);
	line-height: 16px;
}

:where(.badge-pill--large) {
	gap: var(--norkron-space-0, 4px);
	padding: var(--norkron-space-0, 4px) var(--norkron-space-2, 12px);
	font-size: var(--norkron-font-size-sm, 14px);
	line-height: 20px;
}

/* shapes */
:where(.badge-pill--pill) { border-radius: var(--norkron-radius-full, 9999px); }
:where(.badge-pill--rounded) { border-radius: var(--norkron-radius-md, 8px); }
:where(.badge-pill--square) { border-radius: var(--norkron-radius-sm, 4px); }

/* tones \u2014 soft bg is ALWAYS paired with its own on-soft fg, so a partially
 * vendored token set can never produce light-on-light (see file header). */
:where(.badge-pill--neutral) {
	--badge-pill__soft-bg: var(--admin-shell__surface-sunken);
	--badge-pill__soft-fg: var(--admin-shell__fg);
	--badge-pill__solid-bg: var(--admin-shell__fg);
	--badge-pill__solid-fg: var(--admin-shell__surface);
	--badge-pill__line: var(--admin-shell__border-strong);
	--badge-pill__dot: var(--admin-shell__fg-muted);
}

:where(.badge-pill--info) {
	--badge-pill__soft-bg: var(--norkron-state-info-soft, Canvas);
	--badge-pill__soft-fg: var(--norkron-state-info-on-soft, CanvasText);
	--badge-pill__solid-bg: var(--norkron-state-info, CanvasText);
	--badge-pill__solid-fg: var(--admin-shell__on-accent);
	--badge-pill__line: var(--norkron-state-info, ButtonBorder);
	--badge-pill__dot: var(--norkron-state-info, CanvasText);
}

:where(.badge-pill--success) {
	--badge-pill__soft-bg: var(--norkron-state-success-soft, Canvas);
	--badge-pill__soft-fg: var(--norkron-state-success-on-soft, CanvasText);
	--badge-pill__solid-bg: var(--norkron-state-success, CanvasText);
	--badge-pill__solid-fg: var(--admin-shell__on-accent);
	--badge-pill__line: var(--norkron-state-success, ButtonBorder);
	--badge-pill__dot: var(--norkron-state-success, CanvasText);
}

:where(.badge-pill--warning) {
	--badge-pill__soft-bg: var(--norkron-state-warning-soft, Canvas);
	--badge-pill__soft-fg: var(--norkron-state-warning-on-soft, CanvasText);
	--badge-pill__solid-bg: var(--norkron-state-warning, CanvasText);
	--badge-pill__solid-fg: var(--admin-shell__on-accent);
	--badge-pill__line: var(--norkron-state-warning, ButtonBorder);
	--badge-pill__dot: var(--norkron-state-warning, CanvasText);
}

:where(.badge-pill--danger) {
	--badge-pill__soft-bg: var(--norkron-state-danger-soft, Canvas);
	--badge-pill__soft-fg: var(--norkron-state-danger-on-soft, CanvasText);
	--badge-pill__solid-bg: var(--norkron-state-danger, Mark);
	--badge-pill__solid-fg: var(--admin-shell__on-accent);
	--badge-pill__line: var(--norkron-state-danger, ButtonBorder);
	--badge-pill__dot: var(--norkron-state-danger, Mark);
}

:where(.badge-pill--announcement, .badge-pill--admin) {
	--badge-pill__soft-bg: var(--badge-pill-admin-50);
	--badge-pill__soft-fg: var(--badge-pill-admin-900);
	--badge-pill__solid-bg: var(--badge-pill-admin-600);
	--badge-pill__solid-fg: var(--admin-shell__on-accent);
	--badge-pill__line: var(--badge-pill-admin-300);
	--badge-pill__dot: var(--badge-pill-admin-500);
}

/* variants */
:where(.badge-pill--soft) {
	background: var(--badge-pill__soft-bg);
	color: var(--badge-pill__soft-fg);
}

:where(.badge-pill--solid) {
	background: var(--badge-pill__solid-bg);
	color: var(--badge-pill__solid-fg);
}

:where(.badge-pill--outline) {
	background: transparent;
	color: var(--badge-pill__soft-fg);
	border-color: var(--badge-pill__line);
}

:where(button.badge-pill) {
	cursor: pointer;
	font: inherit;
	font-size: inherit;
	font-weight: 500;
	transition: opacity var(--admin-shell__transition) ease;
}

:where(button.badge-pill:hover) {
	opacity: var(--norkron-opacity-strong, 0.9);
}

:where(button.badge-pill:focus-visible) {
	outline: var(--norkron-focus-ring-width, 2px) solid var(--badge-pill-ring);
	outline-offset: var(--norkron-focus-ring-offset, 2px);
}

:where(.badge-pill--disabled) {
	opacity: var(--norkron-opacity-muted, 0.7);
	cursor: not-allowed;
}

:where(.badge-pill__dot) {
	display: inline-block;
	flex-shrink: 0;
	border-radius: var(--norkron-radius-full, 9999px);
	background: var(--badge-pill__dot);
}

:where(.badge-pill--solid) :where(.badge-pill__dot) {
	background: var(--badge-pill__solid-fg);
}

:where(.badge-pill--small) :where(.badge-pill__dot) { width: 6px; height: 6px; }
:where(.badge-pill--medium) :where(.badge-pill__dot) { width: 8px; height: 8px; }
:where(.badge-pill--large) :where(.badge-pill__dot) { width: 10px; height: 10px; }

:where(.badge-pill__icon) {
	display: inline-flex;
	align-items: center;
	flex-shrink: 0;
}

:where(.badge-pill__label) {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

/* \u2550\u2550 kpi-card \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 */
:where(.kpi-card) {
	display: flex;
	flex-direction: column;
	gap: var(--norkron-space-1, 8px);
	box-sizing: border-box;
	text-align: start;
	background: var(--kpi-card-bg);
	color: var(--kpi-card-text);
	border: 1px solid var(--kpi-card-border);
	border-radius: var(--norkron-radius-lg, 12px);
	font-family: var(--norkron-font-sans, inherit);
}

:where(.kpi-card--neutral) {
	border-color: var(--kpi-card-border);
}

:where(.kpi-card--small) { padding: var(--norkron-space-2, 12px); }
:where(.kpi-card--medium) { padding: var(--norkron-space-3, 16px); }
:where(.kpi-card--large) { padding: var(--norkron-space-5, 32px); }

:where(.kpi-card--success) { border-inline-start: 4px solid var(--norkron-state-success, CanvasText); }
:where(.kpi-card--warning) { border-inline-start: 4px solid var(--norkron-state-warning, CanvasText); }
:where(.kpi-card--danger) { border-inline-start: 4px solid var(--norkron-state-danger, Mark); }

:where(button.kpi-card) {
	cursor: pointer;
	font: inherit;
	transition: border-color var(--admin-shell__transition) ease,
		box-shadow var(--admin-shell__transition) ease;
}

:where(.kpi-card--clickable:hover) {
	border-color: var(--kpi-card-accent);
	box-shadow: var(--norkron-shadow-sm, 0 1px 2px 0 rgba(0, 0, 0, 0.05));
}

:where(.kpi-card--clickable:focus-visible) {
	outline: var(--norkron-focus-ring-width, 2px) solid var(--kpi-card-accent);
	outline-offset: var(--norkron-focus-ring-offset, 2px);
}

:where(.kpi-card__label) {
	font-weight: 500;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	color: var(--kpi-card-text-muted);
}

:where(.kpi-card--small) :where(.kpi-card__label) { font-size: var(--admin-shell__font-3xs, 10px); }
:where(.kpi-card--medium) :where(.kpi-card__label) { font-size: var(--norkron-font-size-xs, 12px); }
:where(.kpi-card--large) :where(.kpi-card__label) { font-size: var(--norkron-font-size-sm, 14px); }

:where(.kpi-card__row) {
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
	gap: var(--norkron-space-1, 8px);
}

:where(.kpi-card__value) {
	font-family: var(--norkron-font-mono, monospace);
	font-weight: 600;
	font-variant-numeric: tabular-nums;
	line-height: 1.15;
}

:where(.kpi-card--small) :where(.kpi-card__value) { font-size: var(--norkron-font-size-xl, 20px); }
:where(.kpi-card--medium) :where(.kpi-card__value) { font-size: var(--norkron-font-size-2xl, 24px); }
:where(.kpi-card--large) :where(.kpi-card__value) { font-size: var(--norkron-font-size-3xl, 32px); }

:where(.kpi-card__delta) {
	display: inline-flex;
	align-items: center;
	gap: var(--norkron-space-0, 4px);
	padding: 2px var(--norkron-space-1, 8px);
	font-size: var(--norkron-font-size-xs, 12px);
	font-weight: 600;
	line-height: 16px;
	border-radius: var(--norkron-radius-full, 9999px);
}

:where(.kpi-card__delta--positive) {
	background: var(--norkron-state-success-soft, Canvas);
	color: var(--norkron-state-success-on-soft, CanvasText);
}

:where(.kpi-card__delta--negative) {
	background: var(--norkron-state-danger-soft, Canvas);
	color: var(--norkron-state-danger-on-soft, CanvasText);
}

:where(.kpi-card__delta--neutral) {
	background: var(--admin-shell__surface-sunken);
	color: var(--kpi-card-text-muted);
}

:where(.kpi-card__delta-period) {
	margin-inline-start: var(--norkron-space-0, 4px);
	font-weight: 400;
	opacity: var(--norkron-opacity-strong, 0.9);
}

:where(.kpi-card__sparkline) {
	width: 100%;
	margin-top: var(--norkron-space-0, 4px);
}

:where(.kpi-card__footer) {
	margin-top: auto;
	font-size: var(--norkron-font-size-xs, 12px);
	color: var(--kpi-card-text-muted);
}

/* \u2550\u2550 stat-strip \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 */
:where(.stat-strip) {
	display: inline-flex;
	align-items: center;
	color: var(--stat-strip-text);
	font-family: var(--norkron-font-sans, inherit);
}

:where(.stat-strip--xsmall) { gap: var(--norkron-space-0, 4px); font-size: var(--admin-shell__font-2xs, 11px); }
:where(.stat-strip--small) { gap: var(--norkron-space-1, 8px); font-size: var(--norkron-font-size-xs, 12px); }
:where(.stat-strip--medium) { gap: var(--norkron-space-1, 8px); font-size: var(--norkron-font-size-sm, 14px); }

:where(.stat-strip--wrap) { flex-wrap: wrap; }
:where(.stat-strip--nowrap) { flex-wrap: nowrap; }
:where(.stat-strip--align-start) { justify-content: flex-start; }
:where(.stat-strip--align-center) { justify-content: center; }
:where(.stat-strip--align-end) { justify-content: flex-end; }
:where(.stat-strip--align-between) { justify-content: space-between; width: 100%; }

:where(.stat-strip__item) {
	display: inline-flex;
	align-items: baseline;
	gap: var(--norkron-space-0, 4px);
}

:where(.stat-strip__separator) {
	color: var(--stat-strip-text-muted);
	user-select: none;
}

:where(.stat-strip__label) {
	color: var(--stat-strip-text-muted);
}

:where(.stat-strip__value) {
	font-family: var(--norkron-font-mono, monospace);
	font-weight: 600;
	font-variant-numeric: tabular-nums;
	color: var(--stat-strip-text);
}

:where(.stat-strip__value--positive) { color: var(--norkron-state-success, CanvasText); }
:where(.stat-strip__value--warning) { color: var(--norkron-state-warning, CanvasText); }
:where(.stat-strip__value--negative) { color: var(--norkron-state-danger, Mark); }
:where(.stat-strip__value--muted) { color: var(--stat-strip-text-muted); }

:where(.stat-strip__hint) {
	color: var(--stat-strip-text-muted);
}

:where(.stat-strip--xsmall) :where(.stat-strip__hint) { font-size: var(--admin-shell__font-3xs, 10px); }
:where(.stat-strip--small) :where(.stat-strip__hint) { font-size: var(--admin-shell__font-3xs, 10px); }
:where(.stat-strip--medium) :where(.stat-strip__hint) { font-size: var(--admin-shell__font-2xs, 11px); }

/* \u2550\u2550 data-panel \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 */
:where(.data-panel) {
	box-sizing: border-box;
	overflow: hidden;
	background: var(--data-panel-bg);
	color: var(--data-panel-text);
	border: 1px solid var(--data-panel-border);
	border-radius: var(--norkron-radius-lg, 12px);
	box-shadow: var(--norkron-shadow-sm, 0 1px 2px 0 rgba(0, 0, 0, 0.05));
	font-family: var(--norkron-font-sans, inherit);
}

:where(.data-panel--neutral) {
	background: var(--data-panel-bg);
	color: var(--data-panel-text);
	border-color: var(--data-panel-border);
}

:where(.data-panel--accent) {
	background: var(--data-panel-accent-50);
	color: var(--data-panel-accent-900);
	border-color: var(--data-panel-accent-300);
}

:where(.data-panel__header) {
	display: flex;
	align-items: flex-start;
	gap: var(--norkron-space-2, 12px);
}

:where(.data-panel--compact) :where(.data-panel__header) { padding: var(--norkron-space-1, 8px) var(--norkron-space-2, 12px); }
:where(.data-panel--comfortable) :where(.data-panel__header) { padding: var(--norkron-space-2, 12px) var(--norkron-space-3, 16px); }
:where(.data-panel--spacious) :where(.data-panel__header) { padding: var(--norkron-space-3, 16px) var(--norkron-space-4, 24px); }

:where(.data-panel__icon) {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 24px;
	height: 24px;
	color: var(--data-panel-text-muted);
}

:where(.data-panel__title-block) {
	flex: 1 1 0;
	min-width: 0;
}

:where(.data-panel__title) {
	font-size: var(--norkron-font-size-sm, 14px);
	font-weight: 600;
	line-height: 20px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

:where(.data-panel__meta) {
	margin-top: var(--norkron-space-0, 4px);
	font-size: var(--norkron-font-size-xs, 12px);
	color: var(--data-panel-text-muted);
}

:where(.data-panel__action) {
	flex-shrink: 0;
}

:where(.data-panel--compact) :where(.data-panel__body) { padding: 0 var(--norkron-space-2, 12px) var(--norkron-space-1, 8px); }
:where(.data-panel--comfortable) :where(.data-panel__body) { padding: 0 var(--norkron-space-3, 16px) var(--norkron-space-2, 12px); }
:where(.data-panel--spacious) :where(.data-panel__body) { padding: 0 var(--norkron-space-4, 24px) var(--norkron-space-3, 16px); }

:where(.data-panel__rows) {
	margin: 0;
}

:where(.data-panel__row) {
	display: flex;
	align-items: baseline;
	gap: var(--norkron-space-2, 12px);
	border-top: 1px solid var(--data-panel-border-subtle);
}

:where(.data-panel__row:first-child) {
	border-top: 0;
}

:where(.data-panel--compact) :where(.data-panel__row) { padding: var(--norkron-space-0, 4px) 0; font-size: var(--norkron-font-size-xs, 12px); }
:where(.data-panel--comfortable) :where(.data-panel__row) { padding: var(--norkron-space-0, 4px) 0; font-size: var(--norkron-font-size-sm, 14px); }
:where(.data-panel--spacious) :where(.data-panel__row) { padding: var(--norkron-space-1, 8px) 0; font-size: var(--norkron-font-size-sm, 14px); }

:where(.data-panel__row-label) {
	flex-shrink: 0;
	margin: 0;
	color: var(--data-panel-text-muted);
}

:where(.data-panel__row-value) {
	flex: 1 1 0;
	min-width: 0;
	margin: 0;
	font-weight: 500;
	color: var(--data-panel-text);
}

:where(.data-panel__row--align-right) :where(.data-panel__row-value) { text-align: end; }
:where(.data-panel__row--align-left) :where(.data-panel__row-value) { text-align: start; }

:where(.data-panel__row-text) {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

:where(.data-panel__row-hint) {
	margin-top: 2px;
	font-size: var(--norkron-font-size-xs, 12px);
	font-weight: 400;
	color: var(--data-panel-text-muted);
}

:where(.data-panel__footer) {
	border-top: 1px solid var(--data-panel-border);
	color: var(--data-panel-text-muted);
}

:where(.data-panel--compact) :where(.data-panel__footer) { padding: var(--norkron-space-1, 8px) var(--norkron-space-2, 12px); font-size: var(--norkron-font-size-xs, 12px); }
:where(.data-panel--comfortable) :where(.data-panel__footer) { padding: var(--norkron-space-1, 8px) var(--norkron-space-3, 16px); font-size: var(--norkron-font-size-sm, 14px); }
:where(.data-panel--spacious) :where(.data-panel__footer) { padding: var(--norkron-space-2, 12px) var(--norkron-space-4, 24px); font-size: var(--norkron-font-size-sm, 14px); }

/* \u2550\u2550 delta-value \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 */
:where(.delta-value) {
	display: inline-flex;
	align-items: center;
	font-weight: 500;
	font-variant-numeric: tabular-nums;
}

:where(.delta-value--block) { display: flex; }

:where(.delta-value--xsmall) { gap: 2px; font-size: var(--admin-shell__font-3xs, 10px); }
:where(.delta-value--small) { gap: var(--norkron-space-0, 4px); font-size: var(--norkron-font-size-xs, 12px); }
:where(.delta-value--medium) { gap: var(--norkron-space-0, 4px); font-size: var(--norkron-font-size-sm, 14px); }
:where(.delta-value--large) { gap: var(--norkron-space-0, 4px); font-size: var(--norkron-font-size-md, 16px); }

:where(.delta-value--positive) { color: var(--delta-value-positive); }
:where(.delta-value--negative) { color: var(--delta-value-negative); }
:where(.delta-value--neutral) { color: var(--delta-value-neutral); }

:where(.delta-value__arrow) {
	display: inline-flex;
	flex-shrink: 0;
	line-height: 1;
}

/* \u2550\u2550 view-container \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 */
:where(.view-container) {
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
	min-width: 0;
	box-sizing: border-box;
	color: var(--admin-shell__fg);
	font-family: var(--norkron-font-sans, inherit);
}

/* \u2550\u2550 page-header \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 */
:where(.page-header) {
	display: flex;
	flex-direction: column;
	gap: var(--norkron-space-1, 8px);
	box-sizing: border-box;
	padding: 0 0 var(--norkron-space-2, 12px);
	color: var(--page-header-fg);
	font-family: var(--norkron-font-sans, inherit);
}

:where(.page-header--bordered) {
	border-bottom: 1px solid var(--page-header-border);
	margin-bottom: var(--norkron-space-3, 16px);
}

:where(.page-header--compact) {
	gap: var(--norkron-space-0, 4px);
	padding-bottom: var(--norkron-space-1, 8px);
	--page-header-title-size: var(--norkron-font-size-xl, 20px);
}

:where(.page-header--spacious) {
	gap: var(--norkron-space-2, 12px);
	padding-bottom: var(--norkron-space-4, 24px);
	--page-header-title-size: var(--norkron-font-size-3xl, 32px);
}

:where(.page-header__breadcrumbs) {
	font-size: var(--norkron-font-size-xs, 12px);
	color: var(--page-header-fg-muted);
}

:where(.page-header__row) {
	display: flex;
	flex-wrap: wrap;
	align-items: flex-start;
	gap: var(--norkron-space-2, 12px);
}

:where(.page-header__titles) {
	flex: 1 1 auto;
	min-width: 0;
}

:where(.page-header__title) {
	margin: 0;
	font-family: var(--page-header-title-font);
	font-size: var(--page-header-title-size);
	font-weight: 600;
	line-height: 1.2;
	color: var(--page-header-fg);
}

:where(.page-header__subtitle) {
	margin: var(--norkron-space-0, 4px) 0 0;
	max-width: var(--norkron-measure-default, 60ch);
	font-size: var(--norkron-font-size-sm, 14px);
	line-height: 20px;
	color: var(--page-header-fg-muted);
}

:where(.page-header__actions) {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: var(--norkron-space-1, 8px);
	margin-inline-start: auto;
}

/* \u2550\u2550 responsive: the tier at which top-nav rows expand \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550
 * Declared behaviour (see src/responsive-contract.ts): below the xl tier the
 * primary/secondary/actions/search rows are 'collapsed' behind the hamburger;
 * at xl and above they are 'full' and the hamburger + drawer are hidden.
 * 1280px is the canonical xl breakpoint. Media queries cannot read a custom
 * property, so --top-nav-expand-at documents the value for consumers who
 * re-declare this block; the query below is the shipped default.
 */
@media (min-width: 1280px) {
	:where(.top-nav__primary, .top-nav__secondary, .top-nav__actions) {
		display: flex;
	}
	:where(.top-nav__search) {
		display: block;
	}
	:where(.top-nav__hamburger),
	:where(.top-nav__drawer) {
		display: none;
	}
}

/* \u2550\u2550 forced colours \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550
 * In forced-colors mode the UA replaces every colour. Keep the structural
 * signals (borders, focus ring, active state) that would otherwise vanish
 * because they were carried by background-color alone.
 */
@media (forced-colors: active) {
	:where(.badge-pill),
	:where(.kpi-card),
	:where(.data-panel),
	:where(.top-nav-link--active) {
		border: 1px solid ButtonBorder;
	}
	:where(.top-nav-link--active) {
		forced-color-adjust: none;
		background: Highlight;
		color: HighlightText;
	}
	:where(.badge-pill__dot) {
		outline: 1px solid CanvasText;
	}
}

/* \u2550\u2550 reduced motion \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 */
@media (prefers-reduced-motion: reduce) {
	:where(.top-nav-link),
	:where(.top-nav__action),
	:where(.top-nav__hamburger),
	:where(button.badge-pill),
	:where(button.kpi-card) {
		transition: none;
	}
}
`;
function injectStyles() {
  if (typeof document === "undefined") return false;
  const root = document.documentElement;
  if (root?.getAttribute(STYLE_OPT_OUT_ATTR) === "off") return false;
  if (document.getElementById(STYLE_ELEMENT_ID) !== null) return false;
  const el = document.createElement("style");
  el.id = STYLE_ELEMENT_ID;
  el.setAttribute("data-norkron-lib", "@norkron/web-admin-shell");
  el.textContent = ADMIN_SHELL_STYLES;
  const head = document.head ?? document.body;
  if (!head) return false;
  head.insertBefore(el, head.firstChild);
  return true;
}

// src/self-check.ts
var LIB_ID = "@norkron/web-admin-shell";
var SEVERITY_RANK = {
  info: 0,
  minor: 1,
  major: 2,
  critical: 3
};
function meetsFloor(finding, floor) {
  return SEVERITY_RANK[finding.severity] >= SEVERITY_RANK[floor];
}
function selectorFor(el) {
  const testid = el.getAttribute("data-testid");
  if (testid) return `[data-testid="${testid}"]`;
  const tag = el.tagName.toLowerCase();
  const cls = el.getAttribute("class")?.split(/\s+/).filter(Boolean).slice(0, 2).join(".");
  return cls ? `${tag}.${cls}` : tag;
}
var BLOCK_ROOT_SELECTOR = BEM_BLOCKS.filter((b) => b !== "admin-shell").map((b) => `.${b}`).join(", ");
var IN_NAMESPACE_SELECTOR = BEM_BLOCKS.map((b) => `[class*="${b}"]`).join(", ");
function isInNamespace(cls) {
  for (const block of BEM_BLOCKS) {
    if (cls === block) return true;
    if (cls.startsWith(`${block}__`) || cls.startsWith(`${block}--`)) return true;
  }
  return false;
}
function isBlockRoot(el) {
  const list = el.getAttribute("class")?.split(/\s+/).filter(Boolean) ?? [];
  return list.some((c) => BEM_BLOCKS.includes(c));
}
function stylesheetPresent(doc) {
  if (doc.getElementById(STYLE_ELEMENT_ID) !== null) return true;
  const sheets = Array.from(doc.styleSheets ?? []);
  for (const sheet of sheets) {
    let rules;
    try {
      rules = sheet.cssRules;
    } catch {
      continue;
    }
    if (!rules) continue;
    for (let i = 0; i < rules.length; i += 1) {
      const text = rules[i]?.cssText ?? "";
      if (text.includes("admin-shell__visually-hidden")) return true;
    }
  }
  return false;
}
function now() {
  return typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
}
function selfCheck(root, opts) {
  const start = now();
  const safeOpts = parseSelfCheckOptions(opts);
  const findings = [];
  try {
    const instances = Array.from(
      root.querySelectorAll('[data-norkron-lib^="@norkron/web-admin-shell"]')
    );
    const blockRoots = Array.from(root.querySelectorAll(BLOCK_ROOT_SELECTOR));
    if (instances.length === 0 && blockRoots.length === 0) return findings;
    for (const inst of instances) {
      const hasMain = inst.querySelector('main, [role="main"]') !== null;
      const hasNav = inst.querySelector('nav, [role="navigation"]') !== null;
      const hasHeader = inst.querySelector('header, [role="banner"]') !== null;
      const missing = [];
      if (!hasMain) missing.push("main");
      if (!hasNav) missing.push("nav");
      if (!hasHeader) missing.push("header");
      if (missing.length > 0) {
        findings.push({
          libId: LIB_ID,
          code: "AS_NO_LANDMARK_ROLES",
          category: "a11y",
          severity: "major",
          message: `Admin shell missing landmark element(s): ${missing.join(", ")}.`,
          selector: selectorFor(inst),
          suggestedFix: "Wrap the routed view in <main>, the nav in <nav>, and the brand row in <header>.",
          extras: { missing }
        });
      }
    }
    const MAX_SCANNED = 500;
    let scanned = 0;
    for (const el of Array.from(root.querySelectorAll(IN_NAMESPACE_SELECTOR))) {
      if (scanned >= MAX_SCANNED) break;
      const classes = el.getAttribute("class")?.split(/\s+/).filter(Boolean) ?? [];
      if (classes.length === 0) continue;
      if (!classes.some(isInNamespace)) continue;
      scanned += 1;
      if (isBlockRoot(el)) continue;
      const foreign = classes.filter((c) => !isInNamespace(c));
      if (foreign.length > 0) {
        findings.push({
          libId: LIB_ID,
          code: "AS_UTILITY_CLASS_LEAK",
          category: "stylesheet",
          severity: "major",
          message: `Element inside a @norkron/web-admin-shell component carries class(es) outside the lib's BEM namespace: ${foreign.join(", ")}. A utility-framework class string in a shared lib is a hard dependency on the consumer's build.`,
          selector: selectorFor(el),
          suggestedFix: "Express the styling as a BEM class in src/styles.ts backed by a --norkron-* token, and emit only that class.",
          extras: { foreign, blocks: classes.filter(isInNamespace) }
        });
      }
    }
    const doc = root.ownerDocument;
    if (blockRoots.length > 0 && doc) {
      if (!stylesheetPresent(doc)) {
        findings.push({
          libId: LIB_ID,
          code: "AS_STYLESHEET_MISSING",
          category: "stylesheet",
          severity: "major",
          message: `${blockRoots.length} @norkron/web-admin-shell element(s) are mounted but the lib's stylesheet is not in the document, so every primitive renders unstyled.`,
          selector: selectorFor(blockRoots[0]),
          suggestedFix: `Import '@norkron/web-admin-shell/styles.css', or drop the data-norkron-admin-shell-styles="off" attribute so components can inject it themselves.`,
          extras: { mounted: blockRoots.length, styleElementId: STYLE_ELEMENT_ID }
        });
      }
      const view = doc.defaultView;
      if (view && typeof view.getComputedStyle === "function" && doc.documentElement) {
        const computed = view.getComputedStyle(doc.documentElement);
        const missing = [];
        for (const token of TOKEN_CONTRACT) {
          if (computed.getPropertyValue(token).trim() === "") missing.push(token);
        }
        if (missing.length > 0) {
          findings.push({
            libId: LIB_ID,
            code: "AS_TOKEN_UNDEFINED",
            category: "theme",
            severity: "minor",
            message: `${missing.length} of ${TOKEN_CONTRACT.length} --norkron-* tokens this lib paints with are undefined in this document (first: ${missing.slice(0, 3).join(", ")}); those rules fall back to CSS system colours.`,
            suggestedFix: "Load @norkron/web-themer's base.css in full. A partially vendored copy is what paired a light background with a light foreground on a live surface.",
            extras: { missing, declared: TOKEN_CONTRACT.length }
          });
        }
      }
    }
    void instances;
  } catch (err) {
    findings.push({
      libId: LIB_ID,
      code: "AS_SELFCHECK_INTERNAL_ERROR",
      category: "identity",
      severity: "info",
      message: `selfCheck threw internally: ${err instanceof Error ? err.message : String(err)}`
    });
  }
  if (now() - start > safeOpts.budgetMs) {
    findings.push({
      libId: LIB_ID,
      code: "AS_SELFCHECK_BUDGET_EXCEEDED",
      category: "perf",
      severity: "info",
      message: `selfCheck exceeded budget ${safeOpts.budgetMs}ms.`,
      extras: { budgetMs: safeOpts.budgetMs, elapsed: now() - start }
    });
  }
  return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
}

// src/version.ts
var LIB_VERSION = "0.5.1";
var useIsomorphicLayoutEffect = typeof document !== "undefined" ? useLayoutEffect : useEffect;
function useAdminShellStyles() {
  useIsomorphicLayoutEffect(() => {
    injectStyles();
  }, []);
}
var WAS_LIB = "@norkron/web-admin-shell";
var WAS_TOKEN = /* @__PURE__ */ Symbol.for("norkron.lib.web-admin-shell");
var { warn: errorsWarn } = createScopedErrors(WAS_LIB, WAS_TOKEN);
var CODES = {
  WAS_INVALID_DELTA_VALUE: {
    description: "DeltaValue received a non-finite `value` prop.",
    severity: "warning",
    docsAnchor: "web-admin-shell/delta-value#invalid-value",
    kind: "contract"
  },
  WAS_INVALID_KPI_DELTA: {
    description: "KpiCard `delta.value` is not a finite number.",
    severity: "warning",
    docsAnchor: "web-admin-shell/kpi-card#invalid-delta",
    kind: "contract"
  },
  WAS_TOPNAV_INVALID_LINK_SPEC: {
    description: "TopNav link spec missing required `href`.",
    severity: "warning",
    docsAnchor: "web-admin-shell/top-nav#invalid-link-spec",
    kind: "contract"
  },
  WAS_ACTION_HANDLER_THREW: {
    description: "TopNav action `onClick` handler threw (caught + reported).",
    severity: "warning",
    docsAnchor: "web-admin-shell/top-nav#action-handler-threw",
    kind: "state"
  },
  WAS_VIEW_SUBTREE_FAILED: {
    description: "ViewContainer ErrorBoundary captured a child render failure.",
    severity: "critical-but-non-throwing",
    docsAnchor: "web-admin-shell/view-container#subtree-failed",
    kind: "state"
  },
  WAS_PAGE_HEADER_INVALID_LEVEL: {
    description: "PageHeader `level` was outside 1-6; an <h7> is not a heading, so the title would drop out of the document outline. Clamped to h1.",
    severity: "warning",
    docsAnchor: "web-admin-shell/page-header#invalid-level",
    kind: "range"
  },
  AS_UTILITY_CLASS_LEAK: {
    description: "selfCheck found a class outside this lib's BEM namespace on an element the lib owns \u2014 i.e. a utility-framework class string has returned, re-creating the consumer-build dependency the v0.5.0 stylesheet removed.",
    severity: "warning",
    docsAnchor: "web-admin-shell/self-check#utility-class-leak",
    kind: "contract"
  },
  AS_TOKEN_UNDEFINED: {
    description: "A --norkron-* token this lib paints with resolves to nothing in the mounted document, so the affected rule falls back to a system colour. A partially vendored web-themer base.css is the usual cause.",
    severity: "warning",
    docsAnchor: "web-admin-shell/self-check#token-undefined",
    kind: "environment"
  },
  AS_STYLESHEET_MISSING: {
    description: `The lib's default stylesheet is absent from the document while its components are mounted, so every primitive renders unstyled. Import '@norkron/web-admin-shell/styles.css' or remove the data-norkron-admin-shell-styles="off" opt-out.`,
    severity: "warning",
    docsAnchor: "web-admin-shell/self-check#stylesheet-missing",
    kind: "environment"
  },
  AS_NO_LANDMARK_ROLES: {
    description: "selfCheck found an admin-shell instance with no landmark roles (header/nav/main/footer). Screen-reader users lose page structure.",
    severity: "warning",
    docsAnchor: "web-admin-shell/self-check#no-landmark-roles",
    kind: "contract"
  },
  AS_SELFCHECK_BUDGET_EXCEEDED: {
    description: "selfCheck exceeded its per-call time budget while walking admin-shell instances; partial results returned.",
    severity: "info",
    docsAnchor: "web-admin-shell/self-check#budget-exceeded",
    kind: "environment"
  },
  AS_SELFCHECK_INTERNAL_ERROR: {
    description: "selfCheck threw internally while inspecting an admin-shell instance. The exception is caught and reported as a finding.",
    severity: "info",
    docsAnchor: "web-admin-shell/self-check#internal-error",
    kind: "environment"
  }
};
var _alreadyRegistered = Object.keys(CODES).every((c) => isCodeRegistered(WAS_LIB, c));
if (!_alreadyRegistered) {
  registerCodes(WAS_LIB, {
    WAS_INVALID_DELTA_VALUE: CODES.WAS_INVALID_DELTA_VALUE,
    WAS_INVALID_KPI_DELTA: CODES.WAS_INVALID_KPI_DELTA,
    WAS_TOPNAV_INVALID_LINK_SPEC: CODES.WAS_TOPNAV_INVALID_LINK_SPEC,
    WAS_ACTION_HANDLER_THREW: CODES.WAS_ACTION_HANDLER_THREW,
    WAS_VIEW_SUBTREE_FAILED: CODES.WAS_VIEW_SUBTREE_FAILED,
    WAS_PAGE_HEADER_INVALID_LEVEL: CODES.WAS_PAGE_HEADER_INVALID_LEVEL,
    AS_UTILITY_CLASS_LEAK: CODES.AS_UTILITY_CLASS_LEAK,
    AS_TOKEN_UNDEFINED: CODES.AS_TOKEN_UNDEFINED,
    AS_STYLESHEET_MISSING: CODES.AS_STYLESHEET_MISSING,
    AS_NO_LANDMARK_ROLES: CODES.AS_NO_LANDMARK_ROLES,
    AS_SELFCHECK_BUDGET_EXCEEDED: CODES.AS_SELFCHECK_BUDGET_EXCEEDED,
    AS_SELFCHECK_INTERNAL_ERROR: CODES.AS_SELFCHECK_INTERNAL_ERROR
  });
}
function warn(input) {
  errorsWarn({
    fn: input.fn,
    code: input.code,
    message: input.message,
    prop: input.prop,
    received: input.received,
    errorCause: input.errorCause
  });
}
var BASE_CLASSES = "top-nav-link";
var ACTIVE_CLASSES = "top-nav-link--active";
var ExternalIcon = () => /* @__PURE__ */ jsxs(
  "svg",
  {
    className: "top-nav-link__external-icon",
    viewBox: "0 0 20 20",
    fill: "currentColor",
    "aria-hidden": "true",
    children: [
      /* @__PURE__ */ jsx("path", { d: "M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" }),
      /* @__PURE__ */ jsx("path", { d: "M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" })
    ]
  }
);
var TopNavLink = memo(function TopNavLink2({
  label,
  href,
  active = false,
  badge,
  icon,
  external,
  onClick,
  testId,
  className,
  as
}) {
  useAdminShellStyles();
  const composed = [BASE_CLASSES, active ? ACTIVE_CLASSES : "", className].filter(Boolean).join(" ");
  const activeBg = useThemeVar("--top-nav-active-bg", "");
  const activeFg = useThemeVar("--top-nav-active-fg", "");
  const themeStyle = useMemo(() => {
    if (!active) return {};
    const s = {};
    if (activeBg) s["--top-nav-active-bg"] = activeBg;
    if (activeFg) s["--top-nav-active-fg"] = activeFg;
    return s;
  }, [active, activeBg, activeFg]);
  const content = /* @__PURE__ */ jsxs(Fragment$1, { children: [
    icon && /* @__PURE__ */ jsx("span", { className: "top-nav-link__icon", "aria-hidden": "true", children: icon }),
    /* @__PURE__ */ jsx("span", { className: "top-nav-link__label", children: label }),
    badge !== void 0 && badge !== null && /* @__PURE__ */ jsx(
      "span",
      {
        "data-testid": testId ? `${testId}-badge` : void 0,
        className: "top-nav-link__badge",
        children: badge
      }
    ),
    external && /* @__PURE__ */ jsx(ExternalIcon, {})
  ] });
  if (as) {
    return as({
      href,
      active,
      className: composed,
      "data-testid": testId,
      children: content
    });
  }
  return /* @__PURE__ */ jsx(
    "a",
    {
      href,
      onClick,
      "aria-current": active ? "page" : void 0,
      "data-testid": testId,
      "data-active": active ? "true" : void 0,
      target: external ? "_blank" : void 0,
      rel: external ? "noopener noreferrer" : void 0,
      className: composed,
      style: themeStyle,
      children: content
    }
  );
});
var HamburgerIcon = ({ open }) => /* @__PURE__ */ jsx(
  "svg",
  {
    className: "top-nav__hamburger-icon",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    viewBox: "0 0 24 24",
    "aria-hidden": "true",
    children: open ? /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M6 18L18 6M6 6l12 12" }) : /* @__PURE__ */ jsx(
      "path",
      {
        strokeLinecap: "round",
        strokeLinejoin: "round",
        d: "M4 6h16M4 12h16M4 18h16"
      }
    )
  }
);
function ActionButton({ action }) {
  const safeClick = () => {
    try {
      action.onClick();
    } catch (err) {
      warn({
        code: "WAS_ACTION_HANDLER_THREW",
        fn: "TopNav.ActionButton",
        message: `Action "${action.id}" onClick threw.`,
        prop: "action.onClick",
        received: action.id,
        errorCause: err
      });
    }
  };
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      onClick: safeClick,
      "aria-label": action.label,
      title: action.label,
      "data-testid": action.testId ?? `top-nav__action-${action.id}`,
      className: "top-nav__action",
      children: [
        action.icon,
        action.badge !== void 0 && action.badge > 0 && /* @__PURE__ */ jsx(
          "span",
          {
            "data-testid": action.testId ? `${action.testId}-badge` : `top-nav__action-${action.id}-badge`,
            className: "top-nav__action-badge",
            children: action.badge
          }
        )
      ]
    }
  );
}
var TopNav = memo(function TopNav2({
  brand,
  links = [],
  secondaryLinks = [],
  actions = [],
  userMenu,
  search,
  density = "comfortable",
  sticky = true,
  slotAbove,
  slotBelow,
  linkAs,
  className,
  testId = "top-nav"
}) {
  useAdminShellStyles();
  const [mobileOpen, setMobileOpen] = useState(false);
  const rawDrawerId = useId();
  const rawLabelId = useId();
  const drawerId = `tn${rawDrawerId.replace(/[^a-zA-Z0-9-]/g, "")}`;
  const drawerLabelId = `tnl${rawLabelId.replace(/[^a-zA-Z0-9-]/g, "")}`;
  const drawerRef = useRef(null);
  const hamburgerRef = useRef(null);
  const primaryNavRef = useRef(null);
  const [primaryFocusIndex, setPrimaryFocusIndex] = useState(0);
  useFocusRestore(mobileOpen);
  const activeAccent = useThemeVar("--top-nav-active-bg", "");
  const focusRing = useThemeVar("--top-nav-ring", "");
  const themeStyle = useMemo(() => {
    const s = {};
    if (activeAccent) s["--top-nav-active-bg"] = activeAccent;
    if (focusRing) s["--top-nav-ring"] = focusRing;
    return s;
  }, [activeAccent, focusRing]);
  const primaryArrow = useArrowKeyNavigation({
    orientation: "horizontal",
    itemCount: links.length,
    activeIndex: primaryFocusIndex,
    loop: true,
    onActivate: (idx) => {
      setPrimaryFocusIndex(idx);
      const nav = primaryNavRef.current;
      if (!nav) return;
      const anchors = nav.querySelectorAll("a[data-testid], a[href]");
      anchors[idx]?.focus();
    }
  });
  const closeMobile = useCallback(() => {
    setMobileOpen(false);
  }, []);
  const toggleMobile = useCallback(() => setMobileOpen((v) => !v), []);
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeMobile();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen, closeMobile]);
  useEffect(() => {
    if (!mobileOpen) return;
    if (typeof document === "undefined") return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);
  useEffect(() => {
    if (!mobileOpen) return;
    const t = setTimeout(() => {
      const drawer = drawerRef.current;
      if (!drawer) return;
      const first = drawer.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      first?.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [mobileOpen]);
  useEffect(() => {
    if (!mobileOpen) return;
    const onClick = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        closeMobile();
      }
    };
    const t = setTimeout(() => document.addEventListener("mousedown", onClick), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onClick);
    };
  }, [mobileOpen, closeMobile]);
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e) => {
      if (e.key !== "Tab") return;
      const drawer = drawerRef.current;
      if (!drawer) return;
      const focusables = Array.from(
        drawer.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute("disabled") && el.tabIndex !== -1);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;
      if (!active || !drawer.contains(active)) {
        e.preventDefault();
        first.focus();
        return;
      }
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileOpen]);
  const handleBrandClick = (e) => {
    if (brand.onClick) {
      e.preventDefault();
      brand.onClick();
    }
  };
  const renderLink = (spec, prefix) => {
    if (!spec.href) {
      warn({
        code: "WAS_TOPNAV_INVALID_LINK_SPEC",
        fn: "TopNav.renderLink",
        message: `Link spec in "${prefix}" row is missing required href.`,
        prop: "href",
        received: spec
      });
    }
    return /* @__PURE__ */ jsx(
      TopNavLink,
      {
        as: linkAs,
        ...spec
      },
      spec.id ?? `${prefix}-${spec.href}-${String(spec.label)}`
    );
  };
  const renderMobileLink = (spec, prefix) => /* @__PURE__ */ jsx(
    TopNavLink,
    {
      as: linkAs,
      label: spec.label,
      href: spec.href,
      active: spec.active,
      badge: spec.badge,
      icon: spec.icon,
      external: spec.external,
      testId: spec.testId ? `${spec.testId}-mobile` : void 0,
      onClick: (e) => {
        closeMobile();
        spec.onClick?.(e);
      },
      className: "top-nav-link--block"
    },
    `m-${spec.id ?? `${prefix}-${spec.href}`}`
  );
  return /* @__PURE__ */ jsxs(
    "header",
    {
      role: "banner",
      "data-testid": testId,
      "data-density": density,
      "data-keep-clear": "true",
      ...withConformance("@norkron/web-admin-shell", LIB_VERSION),
      className: [
        "top-nav",
        density === "compact" ? "top-nav--compact" : "top-nav--comfortable",
        sticky ? "top-nav--sticky" : "",
        className
      ].filter(Boolean).join(" "),
      style: {
        paddingTop: "var(--top-nav-safe-area-top, 0)",
        paddingInlineStart: "var(--top-nav-safe-area-left, 0)",
        paddingInlineEnd: "var(--top-nav-safe-area-right, 0)",
        ...themeStyle
      },
      children: [
        slotAbove && /* @__PURE__ */ jsx("div", { "data-testid": `${testId}-slot-above`, className: "top-nav__slot-above", children: slotAbove }),
        /* @__PURE__ */ jsxs("div", { className: "top-nav__bar", children: [
          /* @__PURE__ */ jsxs(
            "a",
            {
              href: brand.href ?? "#",
              onClick: handleBrandClick,
              "data-testid": `${testId}-brand`,
              className: "top-nav__brand",
              children: [
                brand.logo && /* @__PURE__ */ jsx("span", { className: "top-nav__brand-logo", "aria-hidden": "true", children: brand.logo }),
                /* @__PURE__ */ jsx("span", { className: "top-nav__brand-label", children: brand.label })
              ]
            }
          ),
          links.length > 0 && /* @__PURE__ */ jsx(
            "nav",
            {
              ref: primaryNavRef,
              role: "navigation",
              "aria-label": "primary",
              "data-testid": `${testId}-primary`,
              className: "top-nav__primary",
              onKeyDown: primaryArrow.onKeyDown,
              children: links.map((l) => renderLink(l, "primary"))
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "top-nav__right", children: [
            search && /* @__PURE__ */ jsx("div", { className: "top-nav__search", "data-testid": `${testId}-search`, children: search }),
            secondaryLinks.length > 0 && /* @__PURE__ */ jsx(
              "nav",
              {
                role: "navigation",
                "aria-label": "secondary",
                "data-testid": `${testId}-secondary`,
                className: "top-nav__secondary",
                children: secondaryLinks.map((l) => renderLink(l, "secondary"))
              }
            ),
            actions.length > 0 && /* @__PURE__ */ jsx(
              "div",
              {
                "data-testid": `${testId}-actions`,
                className: "top-nav__actions",
                children: actions.map((a) => /* @__PURE__ */ jsx(ActionButton, { action: a }, a.id))
              }
            ),
            userMenu && /* @__PURE__ */ jsx("div", { "data-testid": `${testId}-user-menu`, className: "top-nav__user-menu", children: userMenu }),
            /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                ref: hamburgerRef,
                onClick: toggleMobile,
                "aria-label": mobileOpen ? "Close navigation" : "Open navigation",
                "aria-expanded": mobileOpen,
                "aria-controls": drawerId,
                "data-testid": `${testId}-hamburger`,
                className: "top-nav__hamburger",
                children: /* @__PURE__ */ jsx(HamburgerIcon, { open: mobileOpen })
              }
            )
          ] })
        ] }),
        slotBelow && /* @__PURE__ */ jsx("div", { "data-testid": `${testId}-slot-below`, className: "top-nav__slot-below", children: slotBelow }),
        mobileOpen && /* @__PURE__ */ jsxs(
          "div",
          {
            ref: drawerRef,
            id: drawerId,
            role: "dialog",
            "aria-modal": "true",
            "aria-labelledby": drawerLabelId,
            "data-testid": `${testId}-drawer`,
            className: "top-nav__drawer",
            children: [
              /* @__PURE__ */ jsx("h2", { id: drawerLabelId, className: "admin-shell__visually-hidden", children: "Site navigation" }),
              search && /* @__PURE__ */ jsx("div", { className: "top-nav__drawer-search", children: search }),
              links.map((l) => renderMobileLink(l, "primary")),
              secondaryLinks.length > 0 && /* @__PURE__ */ jsx("div", { className: "top-nav__drawer-group", children: secondaryLinks.map((l) => renderMobileLink(l, "secondary")) }),
              actions.length > 0 && /* @__PURE__ */ jsx("div", { className: "top-nav__drawer-actions", children: actions.map((a) => /* @__PURE__ */ jsx(ActionButton, { action: a }, a.id)) })
            ]
          }
        )
      ]
    }
  );
});
var TONE_MOD = {
  neutral: "badge-pill--neutral",
  info: "badge-pill--info",
  success: "badge-pill--success",
  warning: "badge-pill--warning",
  danger: "badge-pill--danger",
  announcement: "badge-pill--announcement",
  admin: "badge-pill--admin"
};
var SIZE_MOD = {
  sm: "badge-pill--small",
  md: "badge-pill--medium",
  lg: "badge-pill--large"
};
var BadgePill = memo(function BadgePill2({
  children,
  tone = "neutral",
  size = "md",
  variant = "soft",
  shape = "pill",
  icon,
  dot,
  onClick,
  disabled,
  live = false,
  testId,
  title,
  className
}) {
  useAdminShellStyles();
  const interactive = typeof onClick === "function";
  const resolvedTestId = testId ?? `badge-pill__${tone}`;
  const adminBg = useThemeVar("--badge-pill-admin-50", "");
  const adminFg = useThemeVar("--badge-pill-admin-900", "");
  const adminAccent = useThemeVar("--badge-pill-admin-600", "");
  const themeStyle = useMemo(() => {
    if (tone !== "admin") return {};
    const s = {};
    if (adminBg) s["--badge-pill-admin-50"] = adminBg;
    if (adminFg) s["--badge-pill-admin-900"] = adminFg;
    if (adminAccent) s["--badge-pill-admin-600"] = adminAccent;
    return s;
  }, [tone, adminBg, adminFg, adminAccent]);
  const baseClass = [
    "badge-pill",
    TONE_MOD[tone],
    SIZE_MOD[size],
    `badge-pill--${variant}`,
    `badge-pill--${shape}`,
    disabled ? "badge-pill--disabled" : "",
    className
  ].filter(Boolean).join(" ");
  const dotEl = dot ? /* @__PURE__ */ jsx(
    "span",
    {
      "data-testid": "badge-pill__dot",
      "aria-hidden": "true",
      className: "badge-pill__dot",
      style: typeof dot === "string" ? { backgroundColor: dot } : void 0
    }
  ) : null;
  const iconEl = icon ? /* @__PURE__ */ jsx("span", { "data-testid": "badge-pill__icon", "aria-hidden": "true", className: "badge-pill__icon", children: icon }) : null;
  const sharedProps = {
    "data-testid": resolvedTestId,
    "data-tone": tone,
    "data-variant": variant,
    "data-size": size,
    title,
    className: baseClass,
    style: themeStyle
  };
  if (interactive) {
    return /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        onClick: disabled ? void 0 : onClick,
        disabled,
        "aria-disabled": disabled || void 0,
        ...sharedProps,
        children: [
          dotEl,
          iconEl,
          /* @__PURE__ */ jsx("span", { className: "badge-pill__label", children })
        ]
      }
    );
  }
  return /* @__PURE__ */ jsxs("span", { ...live ? { role: "status" } : {}, ...sharedProps, children: [
    dotEl,
    iconEl,
    /* @__PURE__ */ jsx("span", { className: "badge-pill__label", children })
  ] });
});
var TONE_MOD2 = {
  neutral: "kpi-card--neutral",
  success: "kpi-card--success",
  warning: "kpi-card--warning",
  danger: "kpi-card--danger"
};
var SIZE_MOD2 = {
  sm: "kpi-card--small",
  md: "kpi-card--medium",
  lg: "kpi-card--large"
};
function resolveTrend(trend, delta) {
  if (trend && trend !== "auto") return trend;
  if (!delta) return "flat";
  if (delta.value > 0) return "up";
  if (delta.value < 0) return "down";
  return "flat";
}
function deltaTone(delta, invert) {
  if (delta.value === 0) return "zero";
  const isPositive = delta.value > 0;
  const good = invert ? !isPositive : isPositive;
  return good ? "positive" : "negative";
}
var DELTA_CHIP = {
  positive: "kpi-card__delta--positive",
  negative: "kpi-card__delta--negative",
  zero: "kpi-card__delta--neutral"
};
var ARROW = {
  up: "\u25B2",
  down: "\u25BC",
  flat: "\u2013"
};
function formatDeltaValue(value, suffix) {
  const s = suffix ?? "%";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value}${s}`;
}
function isRichTooltipOptions(t) {
  if (!t || typeof t !== "object") return false;
  const obj = t;
  return "content" in obj;
}
var KpiCard = memo(function KpiCard2({
  label,
  value,
  delta,
  trend,
  invertDelta = false,
  sparkline,
  footer,
  tone = "neutral",
  size = "md",
  onClick,
  testId,
  className,
  loading = false,
  tooltip
}) {
  useAdminShellStyles();
  const resolvedTrend = resolveTrend(trend, delta);
  const tid = testId ?? "kpi-card";
  const clickable = typeof onClick === "function";
  const deltaPositiveColor = useThemeVar("--kpi-card-delta-positive", "");
  const deltaNegativeColor = useThemeVar("--kpi-card-delta-negative", "");
  if (delta && !Number.isFinite(delta.value)) {
    warn({
      code: "WAS_INVALID_KPI_DELTA",
      fn: "KpiCard",
      message: `delta.value must be a finite number, received ${String(delta.value)}.`,
      prop: "delta.value",
      received: delta.value
    });
  }
  const themeStyle = useMemo(() => {
    const s = {};
    if (deltaPositiveColor) s["--kpi-card-delta-positive"] = deltaPositiveColor;
    if (deltaNegativeColor) s["--kpi-card-delta-negative"] = deltaNegativeColor;
    return s;
  }, [deltaPositiveColor, deltaNegativeColor]);
  const baseClasses = [
    "kpi-card",
    SIZE_MOD2[size],
    TONE_MOD2[tone],
    clickable ? "kpi-card--clickable" : "",
    className
  ].filter(Boolean).join(" ");
  const Tag = clickable ? "button" : "div";
  const labelNode = /* @__PURE__ */ jsx("div", { "data-testid": `${tid}__label`, className: "kpi-card__label", children: label });
  const wrappedLabel = tooltip ? (() => {
    const baseProps = isRichTooltipOptions(tooltip) ? tooltip : { content: tooltip };
    return /* @__PURE__ */ jsx(
      RichTooltip,
      {
        testId: `${tid}__label-tooltip`,
        ...baseProps,
        trigger: labelNode
      }
    );
  })() : labelNode;
  return /* @__PURE__ */ jsxs(
    Tag,
    {
      type: clickable ? "button" : void 0,
      onClick: clickable ? onClick : void 0,
      "data-testid": tid,
      "data-tone": tone,
      "data-size": size,
      "data-trend": resolvedTrend,
      "data-loading": loading || void 0,
      className: baseClasses,
      style: themeStyle,
      children: [
        wrappedLabel,
        loading ? /* @__PURE__ */ jsx("div", { "data-testid": `${tid}__loading`, className: "kpi-card__loading", children: /* @__PURE__ */ jsx(LoadingState, {}) }) : /* @__PURE__ */ jsxs("div", { className: "kpi-card__row", children: [
          /* @__PURE__ */ jsx("div", { "data-testid": `${tid}__value`, className: "kpi-card__value", children: value }),
          delta != null && Number.isFinite(delta.value) && /* @__PURE__ */ jsxs(
            "span",
            {
              "data-testid": `${tid}__delta`,
              "data-delta-tone": deltaTone(delta, invertDelta),
              className: ["kpi-card__delta", DELTA_CHIP[deltaTone(delta, invertDelta)]].join(" "),
              children: [
                /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: ARROW[resolvedTrend] }),
                /* @__PURE__ */ jsx("span", { children: formatDeltaValue(delta.value, delta.suffix) }),
                delta.period && /* @__PURE__ */ jsx("span", { className: "kpi-card__delta-period", children: delta.period })
              ]
            }
          )
        ] }),
        !loading && sparkline && /* @__PURE__ */ jsx("div", { "data-testid": `${tid}__sparkline`, className: "kpi-card__sparkline", children: sparkline }),
        footer && /* @__PURE__ */ jsx(
          "div",
          {
            "data-testid": `${tid}__footer`,
            className: "kpi-card__footer",
            children: footer
          }
        )
      ]
    }
  );
});
function stableItemKey(item, idx) {
  if (item.id) return item.id;
  if (item.testId) return item.testId;
  if (typeof item.label === "string" || typeof item.label === "number") {
    return `label:${String(item.label)}`;
  }
  return `idx:${idx}`;
}
var TONE_VALUE_CLASSES = {
  neutral: "",
  success: "stat-strip__value--positive",
  warning: "stat-strip__value--warning",
  danger: "stat-strip__value--negative",
  muted: "stat-strip__value--muted"
};
var SIZE_MOD3 = {
  xs: "stat-strip--xsmall",
  sm: "stat-strip--small",
  md: "stat-strip--medium"
};
var StatStrip = memo(function StatStrip2({
  items,
  separator = "\xB7",
  size = "sm",
  align = "start",
  wrap = true,
  ariaLabel = "statistics",
  className,
  testId,
  loading = false,
  empty
}) {
  useAdminShellStyles();
  const resolvedTestId = testId ?? "stat-strip";
  if (loading) {
    return /* @__PURE__ */ jsx(
      "div",
      {
        "data-testid": `${resolvedTestId}__loading`,
        "data-size": size,
        className: ["stat-strip", SIZE_MOD3[size], "stat-strip--loading", className].filter(Boolean).join(" "),
        role: "status",
        "aria-busy": "true",
        "aria-label": ariaLabel,
        children: /* @__PURE__ */ jsx(LoadingState, {})
      }
    );
  }
  if (items.length === 0) {
    if (empty === null) return null;
    return /* @__PURE__ */ jsx(
      "div",
      {
        "data-testid": `${resolvedTestId}__empty`,
        "data-size": size,
        className: ["stat-strip", SIZE_MOD3[size], "stat-strip--empty", className].filter(Boolean).join(" "),
        role: "group",
        "aria-label": ariaLabel,
        children: empty ?? /* @__PURE__ */ jsx(EmptyState, {})
      }
    );
  }
  const containerClass = [
    "stat-strip",
    SIZE_MOD3[size],
    `stat-strip--align-${align}`,
    wrap ? "stat-strip--wrap" : "stat-strip--nowrap",
    className
  ].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-testid": resolvedTestId,
      "data-size": size,
      "data-align": align,
      className: containerClass,
      role: "group",
      "aria-label": ariaLabel,
      children: items.map((item, idx) => {
        const tone = item.tone ?? "neutral";
        const itemTestId = item.testId ?? `stat-strip__item-${idx}`;
        const itemKey = stableItemKey(item, idx);
        const valueClass = ["stat-strip__value", TONE_VALUE_CLASSES[tone]].filter(Boolean).join(" ");
        return /* @__PURE__ */ jsxs(Fragment, { children: [
          idx > 0 ? /* @__PURE__ */ jsx(
            "span",
            {
              "data-testid": `stat-strip__separator-${idx}`,
              "aria-hidden": "true",
              className: "stat-strip__separator",
              children: separator
            }
          ) : null,
          /* @__PURE__ */ jsxs(
            "span",
            {
              "data-testid": itemTestId,
              "data-tone": tone,
              className: "stat-strip__item",
              children: [
                /* @__PURE__ */ jsx("span", { className: "stat-strip__label", children: item.label }),
                /* @__PURE__ */ jsx("span", { className: valueClass, children: item.value }),
                item.hint != null ? /* @__PURE__ */ jsx("span", { "data-testid": `${itemTestId}-hint`, className: "stat-strip__hint", children: item.hint }) : null
              ]
            }
          )
        ] }, itemKey);
      })
    }
  );
});
var TONE_MOD3 = {
  neutral: "data-panel--neutral",
  accent: "data-panel--accent"
};
var DENSITY_MOD = {
  compact: "data-panel--compact",
  comfortable: "data-panel--comfortable",
  spacious: "data-panel--spacious"
};
function joinClasses(...classes) {
  return classes.filter(Boolean).join(" ");
}
var DataPanel = memo(function DataPanel2({
  title,
  icon,
  meta,
  action,
  rows,
  children,
  footer,
  density = "comfortable",
  tone = "neutral",
  className,
  testId,
  empty
}) {
  useAdminShellStyles();
  const tid = testId ?? "data-panel";
  const hasRows = !!rows && rows.length > 0;
  const hasBody = hasRows || children !== void 0;
  const isEmpty = !hasBody && empty !== null;
  return /* @__PURE__ */ jsxs(
    "section",
    {
      "data-testid": tid,
      "data-density": density,
      "data-tone": tone,
      className: joinClasses("data-panel", DENSITY_MOD[density], TONE_MOD3[tone], className),
      children: [
        /* @__PURE__ */ jsxs("header", { "data-testid": `${tid}__header`, className: "data-panel__header", children: [
          icon && /* @__PURE__ */ jsx("div", { "data-testid": `${tid}__icon`, "aria-hidden": "true", className: "data-panel__icon", children: icon }),
          /* @__PURE__ */ jsxs("div", { className: "data-panel__title-block", children: [
            /* @__PURE__ */ jsx("div", { "data-testid": `${tid}__title`, className: "data-panel__title", children: title }),
            meta !== void 0 && meta !== null && meta !== false && /* @__PURE__ */ jsx("div", { "data-testid": `${tid}__meta`, className: "data-panel__meta", children: meta })
          ] }),
          action && /* @__PURE__ */ jsx("div", { "data-testid": `${tid}__action`, className: "data-panel__action", children: action })
        ] }),
        isEmpty && /* @__PURE__ */ jsx(
          "div",
          {
            "data-testid": `${tid}__empty`,
            className: "data-panel__body data-panel__body--empty",
            children: empty ?? /* @__PURE__ */ jsx(EmptyState, {})
          }
        ),
        hasBody && /* @__PURE__ */ jsxs("div", { "data-testid": `${tid}__body`, className: "data-panel__body", children: [
          rows && rows.length > 0 && /* @__PURE__ */ jsx("dl", { "data-testid": `${tid}__rows`, className: "data-panel__rows", children: rows.map((row, idx) => {
            const align = row.align ?? "right";
            const rowTid = row.testId ?? `${tid}__row-${idx}`;
            return /* @__PURE__ */ jsxs(
              "div",
              {
                "data-testid": rowTid,
                "data-align": align,
                className: joinClasses(
                  "data-panel__row",
                  `data-panel__row--align-${align}`
                ),
                children: [
                  /* @__PURE__ */ jsx("dt", { className: "data-panel__row-label", children: row.label }),
                  /* @__PURE__ */ jsxs("dd", { className: "data-panel__row-value", children: [
                    /* @__PURE__ */ jsx("div", { className: "data-panel__row-text", children: row.value }),
                    row.hint !== void 0 && row.hint !== null && row.hint !== false && /* @__PURE__ */ jsx(
                      "div",
                      {
                        "data-testid": `${rowTid}-hint`,
                        className: "data-panel__row-hint",
                        children: row.hint
                      }
                    )
                  ] })
                ]
              },
              rowTid
            );
          }) }),
          children
        ] }),
        footer && /* @__PURE__ */ jsx("footer", { "data-testid": `${tid}__footer`, className: "data-panel__footer", children: footer })
      ]
    }
  );
});
function isRichTooltipOptions2(t) {
  if (!t || typeof t !== "object") return false;
  const obj = t;
  return "content" in obj;
}
var SIZE_MOD4 = {
  xs: "delta-value--xsmall",
  sm: "delta-value--small",
  md: "delta-value--medium",
  lg: "delta-value--large"
};
function nf(locale, options) {
  try {
    return new Intl.NumberFormat(locale, options);
  } catch {
    return new Intl.NumberFormat("en-US", options);
  }
}
function formatBytes(v, _locale) {
  return formatBytes$1(v, { precision: 1 });
}
function formatDuration(v, locale) {
  const abs = Math.abs(v);
  const fmtInt = nf(locale, { maximumFractionDigits: 0 });
  const sign = v < 0 ? "-" : "";
  if (abs >= 3600) return `${sign}${fmtInt.format(Math.round(abs / 3600))}h`;
  if (abs >= 60) return `${sign}${fmtInt.format(Math.round(abs / 60))}m`;
  return `${sign}${fmtInt.format(abs)}s`;
}
function formatValue(v, format, locale, formatOptions) {
  const fmt = (opts = {}) => nf(locale, { ...opts, ...formatOptions ?? {} }).format(v);
  switch (format) {
    case "count":
      return fmt();
    case "pct":
      return `${fmt()}%`;
    case "pp":
      return `${fmt()}pp`;
    case "pixels":
      return `${fmt()}px`;
    case "ms":
      return `${fmt()}ms`;
    case "bytes":
      return formatBytes(v);
    case "duration":
      return formatDuration(v, locale);
  }
}
function resolveTone(value, invert, tone) {
  if (tone === "neutral" || value === 0) return "slate";
  const positive = value > 0;
  const good = invert ? !positive : positive;
  return good ? "emerald" : "rose";
}
var TONE_CLASSES = {
  emerald: "delta-value--positive",
  rose: "delta-value--negative",
  slate: "delta-value--neutral"
};
function arrowFor(value) {
  if (value > 0) return "\u25B2";
  if (value < 0) return "\u25BC";
  return "\u2013";
}
var DeltaValue = memo(function DeltaValue2({
  value,
  format = "count",
  formatter,
  showSign = true,
  invert = false,
  showArrow = true,
  tone = "auto",
  size = "md",
  inline = true,
  ariaLabel,
  locale = "en-US",
  formatOptions,
  className,
  testId,
  tooltip
}) {
  useAdminShellStyles();
  const finite = Number.isFinite(value);
  if (!finite) {
    warn({
      code: "WAS_INVALID_DELTA_VALUE",
      fn: "DeltaValue",
      message: `value must be a finite number, received ${String(value)}.`,
      prop: "value",
      received: value
    });
  }
  const safeValue = finite ? value : 0;
  const resolved = resolveTone(safeValue, invert, tone);
  const formatted = !finite ? "\u2014" : formatter ? formatter(safeValue) : formatValue(safeValue, format, locale, formatOptions);
  const signed = !finite ? formatted : showSign && safeValue > 0 && !formatted.startsWith("+") ? `+${formatted}` : formatted;
  const Tag = inline ? "span" : "div";
  const classes = [
    "delta-value",
    SIZE_MOD4[size],
    inline ? "" : "delta-value--block",
    TONE_CLASSES[resolved],
    className
  ].filter(Boolean).join(" ");
  const chip = /* @__PURE__ */ jsxs(
    Tag,
    {
      className: classes,
      "data-testid": testId ?? `delta-value__${resolved}`,
      "data-tone": resolved,
      "data-sign": !finite ? "invalid" : safeValue > 0 ? "positive" : safeValue < 0 ? "negative" : "zero",
      "data-size": size,
      "data-locale": locale,
      "aria-label": ariaLabel,
      children: [
        showArrow && finite && /* @__PURE__ */ jsx(
          "span",
          {
            "data-testid": "delta-value__arrow",
            "aria-hidden": "true",
            className: "delta-value__arrow",
            children: arrowFor(safeValue)
          }
        ),
        /* @__PURE__ */ jsx(
          "span",
          {
            className: "delta-value__direction admin-shell__visually-hidden",
            "data-testid": "delta-value__direction",
            children: !finite ? "invalid " : safeValue > 0 ? "increased by " : safeValue < 0 ? "decreased by " : "unchanged "
          }
        ),
        /* @__PURE__ */ jsx("span", { "data-testid": "delta-value__text", className: "delta-value__text", children: signed })
      ]
    }
  );
  if (tooltip) {
    const baseProps = isRichTooltipOptions2(tooltip) ? tooltip : { content: tooltip };
    return /* @__PURE__ */ jsx(
      RichTooltip,
      {
        testId: (testId ?? `delta-value__${resolved}`) + "-tooltip",
        ...baseProps,
        trigger: chip
      }
    );
  }
  return chip;
});
var ViewContainer = memo(function ViewContainer2({
  children,
  className,
  fallback,
  resetKeys,
  withBoundary = true,
  onReset
}) {
  useAdminShellStyles();
  const wrapped = withBoundary ? /* @__PURE__ */ jsx(
    ErrorBoundary,
    {
      fallback: fallback ?? ((error, reset) => /* @__PURE__ */ jsx(DefaultErrorFallback, { error, reset })),
      onError: (err, info, correlationId) => {
        warn({
          code: "WAS_VIEW_SUBTREE_FAILED",
          fn: "ViewContainer",
          message: `View subtree threw: ${err.message}`,
          prop: "children",
          received: { correlationId, componentStack: info.componentStack },
          errorCause: err
        });
      },
      resetKeys,
      onReset,
      children
    }
  ) : children;
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: ["view-container", className].filter(Boolean).join(" "),
      "data-testid": "view-container",
      children: wrapped
    }
  );
});
var DENSITY_MOD2 = {
  compact: "page-header--compact",
  comfortable: "page-header--comfortable",
  spacious: "page-header--spacious"
};
var VALID_LEVELS = [1, 2, 3, 4, 5, 6];
function joinClasses2(...classes) {
  return classes.filter(Boolean).join(" ");
}
var PageHeader = memo(function PageHeader2({
  title,
  subtitle,
  actions,
  breadcrumbs,
  level = 1,
  density = "comfortable",
  bordered = false,
  titleId,
  className,
  testId
}) {
  useAdminShellStyles();
  const tid = testId ?? "page-header";
  let safeLevel = level;
  if (!VALID_LEVELS.includes(level)) {
    warn({
      code: "WAS_PAGE_HEADER_INVALID_LEVEL",
      fn: "PageHeader",
      message: `level must be 1-6, received ${String(level)}; rendering h1.`,
      prop: "level",
      received: level
    });
    safeLevel = 1;
  }
  const Heading = `h${safeLevel}`;
  return /* @__PURE__ */ jsxs(
    "header",
    {
      "data-testid": tid,
      "data-density": density,
      "data-level": safeLevel,
      ...withConformance("@norkron/web-admin-shell", LIB_VERSION),
      className: joinClasses2(
        "page-header",
        DENSITY_MOD2[density],
        bordered && "page-header--bordered",
        className
      ),
      children: [
        breadcrumbs !== void 0 && breadcrumbs !== null && breadcrumbs !== false && /* @__PURE__ */ jsx("div", { "data-testid": `${tid}__breadcrumbs`, className: "page-header__breadcrumbs", children: breadcrumbs }),
        /* @__PURE__ */ jsxs("div", { className: "page-header__row", children: [
          /* @__PURE__ */ jsxs("div", { className: "page-header__titles", children: [
            /* @__PURE__ */ jsx(
              Heading,
              {
                id: titleId,
                "data-testid": `${tid}__title`,
                className: "page-header__title",
                children: title
              }
            ),
            subtitle !== void 0 && subtitle !== null && subtitle !== false && /* @__PURE__ */ jsx("p", { "data-testid": `${tid}__subtitle`, className: "page-header__subtitle", children: subtitle })
          ] }),
          actions !== void 0 && actions !== null && actions !== false && /* @__PURE__ */ jsx("div", { "data-testid": `${tid}__actions`, className: "page-header__actions", children: actions })
        ] })
      ]
    }
  );
});
var RESPONSIVE_CONTRACT = {
  lib: "@norkron/web-admin-shell",
  supportedBreakpoints: ["xs", "sm", "md", "lg", "xl", "2xl", "3xl"],
  breakpointBehavior: {
    // Grounded in src/styles.ts — the `@media (min-width: 1280px)` block is
    // the only media query this lib emits. Below it the TopNav link rows,
    // action cluster and search slot are display:none and navigation is
    // reachable only through the hamburger + drawer.
    xs: "collapsed",
    sm: "collapsed",
    md: "collapsed",
    lg: "collapsed",
    xl: "full",
    "2xl": "full",
    "3xl": "full"
  },
  containerQueries: [],
  // The stylesheet-and-token rules the lib self-checks. AS_STYLESHEET_MISSING
  // is the one that makes an unstyled render (the v0.4.x failure mode)
  // machine-detectable instead of a visual judgement call.
  rwdSelfCheckCodes: ["AS_STYLESHEET_MISSING", "AS_UTILITY_CLASS_LEAK", "AS_TOKEN_UNDEFINED"]
};
registerResponsiveContract(RESPONSIVE_CONTRACT.lib, RESPONSIVE_CONTRACT);

// src/index.ts
registerLazy(
  "@norkron/web-admin-shell",
  () => selfCheck
);

export { ADMIN_SHELL_STYLES, BEM_BLOCKS, BadgePill, DataPanel, DeltaValue, KpiCard, LIB_VERSION, PageHeader, RESPONSIVE_CONTRACT, STYLE_ELEMENT_ID, STYLE_OPT_OUT_ATTR, StatStrip, TOKEN_CONTRACT, TopNav, TopNavLink, ViewContainer, injectStyles, selfCheck, useAdminShellStyles };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map